import { Info, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Button } from '@/components/shadcn/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { GenericInAppNotificationDefinitionResponse } from '@/api/model/genericInAppNotificationDefinitionResponse'

interface Props {
  isLoading: boolean
  isError: boolean
  items: GenericInAppNotificationDefinitionResponse[] | undefined | null
  onEdit: (item: GenericInAppNotificationDefinitionResponse) => void
  onDetail: (item: GenericInAppNotificationDefinitionResponse) => void
  onDelete: (item: GenericInAppNotificationDefinitionResponse) => void
}

export function NotificationsDataTable({ isLoading, isError, items, onEdit, onDetail, onDelete }: Props) {
  const { t } = useTranslation('notifications')

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <TableHead>{t('list.columns.title')}</TableHead>
          <TableHead>{t('list.columns.message')}</TableHead>
          <TableHead>{t('list.columns.createdAt')}</TableHead>
          <TableHead>{t('list.columns.read')}</TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable isLoading={isLoading} isError={isError} isEmpty={!items || items.length === 0} colSpan={5}>
          {items?.map((n) => (
          <TableRow key={n.id}>
            <TableCell className="font-medium">{n.title ?? '—'}</TableCell>
            <TableCell className="max-w-xs truncate text-muted-foreground">
              {n.message ?? '—'}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {n.creationTimestamp ? dayjs(n.creationTimestamp).format('DD.MM.YYYY') : '—'}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1.5">
                <span className="text-sm">{n.readCount} / {n.totalCount}</span>
                {n.readCount > 0 && (
                  <button
                    onClick={() => onDetail(n)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title={t('list.detailsTooltip')}
                  >
                    <Info className="size-4" />
                  </button>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit(n)}>
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onDelete(n)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
